import "@saleor/macaw-ui-next/style";
import "./index.css";

import { ApolloProvider } from "@apollo/client";
import { history, Route, Router } from "@dashboard/components/Router";
import { AppExtensionPopupProvider } from "@dashboard/extensions/components/AppExtensionContext/AppExtensionContextProvider";
import { ExtensionsPaths, extensionsSection } from "@dashboard/extensions/urls";
import { PermissionEnum } from "@dashboard/graphql";
import useAppState from "@dashboard/hooks/useAppState";
import { pageListPath } from "@dashboard/modeling/urls";
import { modelTypesPath } from "@dashboard/modelTypes/urls";
import { refundsSettingsPath } from "@dashboard/refundsSettings/urls";
import { sellerPayoutStatementsPath } from "@dashboard/sellerPayoutStatements/urls";
import { structuresListPath } from "@dashboard/structures/urls";
import { ThemeProvider } from "@dashboard/theme";
import { OnboardingProvider } from "@dashboard/welcomePage/WelcomePageOnboarding/onboardingContext";
import { ThemeProvider as LegacyThemeProvider } from "@saleor/macaw-ui";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import { IntlShape, useIntl } from "react-intl";
import { matchPath, Redirect, Switch, useLocation } from "react-router-dom";

import { analyticsPath } from "./analytics/urls";
import { attributeSection } from "./attributes/urls";
import AuthProvider from "./auth/AuthProvider";
import LoginLoading from "./auth/components/LoginLoading/LoginLoading";
import SectionRoute from "./auth/components/SectionRoute";
import { useAuthRedirection } from "./auth/hooks/useAuthRedirection";
import {
  loginCallbackPath,
  newPasswordPath,
  passwordResetPath,
  passwordResetSuccessPath,
} from "./auth/urls";
import { useUser } from "./auth/useUser";
import { channelsSection } from "./channels/urls";
import AppLayout from "./components/AppLayout";
import useAppChannel, { AppChannelProvider } from "./components/AppLayout/AppChannelContext";
import { DevModeProvider } from "./components/DevModePanel/DevModeProvider";
import ErrorPage from "./components/ErrorPage";
import ExitFormDialogProvider from "./components/Form/ExitFormDialogProvider";
import { legacyRedirects } from "./components/LegacyRedirects";
import { LocaleProvider } from "./components/Locale";
import { NavigatorSearchProvider } from "./components/NavigatorSearch/NavigatorSearchProvider";
import { NotificationProvider } from "./components/notifications";
import { ProductAnalytics } from "./components/ProductAnalytics";
import { SavebarRefProvider } from "./components/Savebar/SavebarRefContext";
import { ShopProvider } from "./components/Shop";
import { WindowTitle } from "./components/WindowTitle";
import { GTM_ID } from "./config";
import { getConfigMenuItemsPermissions } from "./configuration/utils";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import { FeatureFlagsProviderWithUser } from "./featureFlags/FeatureFlagsProvider";
import { giftCardsSectionUrlName } from "./giftCards/urls";
import { apolloClient } from "./graphql/client";
import { useLocationState } from "./hooks/useLocationState";
import { commonMessages, sectionNames } from "./intl";
import { NotFound } from "./NotFound";
import { errorTracker } from "./services/errorTracking";
import { MANAGE_SUBSCRIPTION_PERMISSION } from "./subscriptions/permissions";
import { paletteOverrides, themeOverrides } from "./themeOverrides";
import { warehouseSection } from "./warehouses/urls";

// Lazy-loaded page sections for code splitting
const AttributeSection = lazy(() => import("./attributes"));
const AnalyticsSection = lazy(() =>
  import("./analytics/AnalyticsSection").then(m => ({ default: m.AnalyticsSection })),
);
const Auth = lazy(() => import("./auth"));
const CategorySection = lazy(() => import("./categories"));
const ChannelsSection = lazy(() => import("./channels"));
const CollectionSection = lazy(() => import("./collections"));
const CustomerSection = lazy(() =>
  import("./customers").then(m => ({ default: m.CustomerSection })),
);
const UserManagementSection = lazy(() =>
  import("./userManagement").then(m => ({ default: m.UserManagementSection })),
);
const MultiVendorRequestsSection = lazy(() =>
  import("./multiVendorRequests").then(m => ({ default: m.MultiVendorRequestsSection })),
);
const UserInterestSection = lazy(() =>
  import("./userInterest").then(m => ({ default: m.UserInterestSection })),
);
const SubscriptionsSection = lazy(() =>
  import("./subscriptions").then(m => ({ default: m.SubscriptionsSection })),
);
const DiscountSection = lazy(() => import("./discounts"));
const ExtensionsSection = lazy(() =>
  import("./extensions").then(m => ({ default: m.ExtensionsSection })),
);
const GiftCardSection = lazy(() => import("./giftCards"));
const LearnDiscoverySection = lazy(() =>
  import("./learnDiscovery").then(m => ({ default: m.LearnDiscoverySection })),
);
const PageSection = lazy(() => import("./modeling"));
const PageTypesSection = lazy(() => import("./modelTypes"));
const OrdersSection = lazy(() => import("./orders"));
const SpecialRequestsSection = lazy(() =>
  import("./specialRequests").then(m => ({ default: m.SpecialRequestsSection })),
);
const PermissionGroupSection = lazy(() =>
  import("./permissionGroups/PermissionGroupsSection").then(m => ({
    default: m.PermissionGroupsSection,
  })),
);
const ProductSection = lazy(() => import("./products"));
const ProductTypesSection = lazy(() => import("./productTypes"));
const SearchSection = lazy(() => import("./search"));
const SellerPayoutStatementsSection = lazy(() =>
  import("./sellerPayoutStatements").then(m => ({ default: m.SellerPayoutStatementsSection })),
);
const ShippingSection = lazy(() => import("./shipping"));
const SiteSettingsSection = lazy(() => import("./siteSettings"));
const StaffSection = lazy(() => import("./staff"));
const NavigationSection = lazy(() => import("./structures"));
const TaxesSection = lazy(() => import("./taxes"));
const TranslationsSection = lazy(() => import("./translations"));
const WarehouseSection = lazy(() => import("./warehouses"));
const ConfigurationSection = lazy(() => import("./configuration"));
const WelcomePage = lazy(() => import("./welcomePage").then(m => ({ default: m.WelcomePage })));
const RefundsSettingsRoute = lazy(() =>
  import("./refundsSettings/route").then(m => ({ default: m.RefundsSettingsRoute })),
);

if (GTM_ID) {
  TagManager.initialize({ gtmId: GTM_ID });
}

errorTracker.init(history);

const getImmediateWindowTitle = (pathname: string, intl: IntlShape): string => {
  const routeTitles = [
    { path: "/", title: sectionNames.home, exact: true },
    { path: analyticsPath, title: sectionNames.analytics },
    { path: sellerPayoutStatementsPath, title: sectionNames.sellerPayoutStatements },
    { path: "/search", title: sectionNames.search },
    { path: "/categories", title: sectionNames.categories },
    { path: "/collections", title: sectionNames.collections },
    { path: "/user-management", title: sectionNames.userManagement },
    { path: "/multi-vendor-requests", title: sectionNames.multiVendorRequests },
    { path: "/user-interests", title: sectionNames.userInterest },
    { path: "/subscriptions", title: sectionNames.subscriptions },
    { path: "/customers", title: sectionNames.customers },
    { path: giftCardsSectionUrlName, title: sectionNames.giftCards },
    { path: "/discounts", title: sectionNames.vouchers },
    { path: pageListPath, title: sectionNames.models },
    { path: "/learn-discover", title: sectionNames.learnDiscovery },
    { path: "/learn-discovery", title: sectionNames.learnDiscovery },
    { path: modelTypesPath, title: sectionNames.modelTypes },
    { path: "/orders", title: sectionNames.orders },
    { path: "/special-requests", title: sectionNames.specialRequests },
    { path: "/products", title: sectionNames.products },
    { path: "/industry-types", title: sectionNames.productTypes },
    { path: "/staff", title: sectionNames.staff },
    { path: "/permission-groups", title: sectionNames.permissionGroups },
    { path: "/site-settings", title: sectionNames.siteSettings },
    { path: refundsSettingsPath, title: sectionNames.refundsSettings },
    { path: "/taxes", title: sectionNames.taxes },
    { path: "/shipping", title: sectionNames.shipping },
    { path: "/translations", title: sectionNames.translations },
    { path: structuresListPath, title: sectionNames.structures },
    { path: attributeSection, title: sectionNames.attributes },
    { path: extensionsSection, title: sectionNames.extensions },
    { path: warehouseSection, title: sectionNames.warehouses },
    { path: channelsSection, title: sectionNames.channels },
    { path: "/configuration", title: sectionNames.configuration },
  ];

  const matchedRoute = routeTitles.find(({ path, exact }) =>
    matchPath(pathname, { path, exact: exact ?? false }),
  );

  return matchedRoute
    ? intl.formatMessage(matchedRoute.title)
    : intl.formatMessage(commonMessages.dashboard);
};

/*
  Handle legacy theming toggle. Since we use new and old macaw,
  we need to handle both theme swticher for a while.
*/
const handleLegacyTheming = (): void => {
  const activeTheme = localStorage.getItem("activeMacawUITheme");

  if (activeTheme === "defaultDark") {
    localStorage.setItem("macaw-ui-theme", "dark");

    return;
  }

  localStorage.setItem("macaw-ui-theme", "light");
};

handleLegacyTheming();

import { Provider as ReduxProvider } from "react-redux";

import { store } from "./store";

const AuthenticatedShopProvider = ({ children }: { children: JSX.Element }) => {
  const { authenticated } = useUser();

  return authenticated ? <ShopProvider>{children}</ShopProvider> : children;
};

const App = (): JSX.Element => (
  <ReduxProvider store={store}>
    <ApolloProvider client={apolloClient}>
      <Router>
        {/* @ts-expect-error legacy types */}
        <LegacyThemeProvider overrides={themeOverrides} palettes={paletteOverrides}>
          <ThemeProvider>
            <LocaleProvider>
              <NotificationProvider>
                <BackgroundTasksProvider>
                  <AppStateProvider>
                    <AuthProvider>
                      <ProductAnalytics>
                        <AppChannelProvider>
                          <AuthenticatedShopProvider>
                            <ExitFormDialogProvider>
                              <DevModeProvider>
                                <NavigatorSearchProvider>
                                  <SavebarRefProvider>
                                    <FeatureFlagsProviderWithUser>
                                      <OnboardingProvider>
                                        <Routes />
                                      </OnboardingProvider>
                                    </FeatureFlagsProviderWithUser>
                                  </SavebarRefProvider>
                                </NavigatorSearchProvider>
                              </DevModeProvider>
                            </ExitFormDialogProvider>
                          </AuthenticatedShopProvider>
                        </AppChannelProvider>
                      </ProductAnalytics>
                    </AuthProvider>
                  </AppStateProvider>
                </BackgroundTasksProvider>
              </NotificationProvider>
            </LocaleProvider>
          </ThemeProvider>
        </LegacyThemeProvider>
      </Router>
    </ApolloProvider>
  </ReduxProvider>
);
const Routes = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const { authenticated, authenticating } = useAuthRedirection();
  const location = useLocation();
  const { channel } = useAppChannel(false);
  const channelLoaded = typeof channel !== "undefined";
  const homePageLoaded = channelLoaded && authenticated;
  const homePageLoading = (authenticated && !channelLoaded) || authenticating;
  const { isAppPath } = useLocationState();
  const immediateWindowTitle = getImmediateWindowTitle(location.pathname, intl);
  const isAuthRoute = [
    passwordResetSuccessPath,
    passwordResetPath,
    newPasswordPath,
    loginCallbackPath,
  ].some(path => matchPath(location.pathname, { path, exact: true }));

  return (
    <>
      <WindowTitle title={immediateWindowTitle} />
      {isAuthRoute ? (
        <Suspense fallback={<LoginLoading />}>
          <Auth />
        </Suspense>
      ) : homePageLoaded ? (
        <AppExtensionPopupProvider>
          <AppLayout fullSize={isAppPath}>
            <ErrorBoundary
              onError={e => {
                const errorId = errorTracker.captureException(e);

                dispatchAppState({
                  payload: {
                    error: "unhandled",
                    errorId,
                  },
                  type: "displayError",
                });
              }}
              fallbackRender={({ resetErrorBoundary }) => (
                <ErrorPage onBack={resetErrorBoundary} onRefresh={() => window.location.reload()} />
              )}
            >
              <Suspense fallback={<LoginLoading />}>
                <Switch>
                  {legacyRedirects}
                  <SectionRoute exact path="/" component={WelcomePage} />
                  <SectionRoute
                    permissions={[]}
                    path={analyticsPath}
                    component={AnalyticsSection}
                  />
                  <SectionRoute
                    permissions={[]}
                    path={sellerPayoutStatementsPath}
                    component={SellerPayoutStatementsSection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PRODUCTS,
                      PermissionEnum.MANAGE_ORDERS,
                      PermissionEnum.MANAGE_PAGES,
                      PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                    ]}
                    matchPermission="any"
                    path="/search"
                    component={SearchSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/categories"
                    component={CategorySection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/collections"
                    component={CollectionSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_USERS]}
                    path="/user-management"
                    component={UserManagementSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_USERS]}
                    path="/user-interests"
                    component={UserInterestSection}
                  />
                  <SectionRoute
                    permissions={[MANAGE_SUBSCRIPTION_PERMISSION]}
                    path="/subscriptions"
                    component={SubscriptionsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_USERS]}
                    path="/customers"
                    component={CustomerSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_GIFT_CARD]}
                    path={giftCardsSectionUrlName}
                    component={GiftCardSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_DISCOUNTS]}
                    path="/discounts"
                    component={DiscountSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PAGES]}
                    path={pageListPath}
                    component={PageSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PAGES]}
                    path="/learn-discovery"
                    component={LearnDiscoverySection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PAGES]}
                    path="/learn-discover"
                    component={LearnDiscoverySection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PAGES,
                      PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                    ]}
                    path={modelTypesPath}
                    component={PageTypesSection}
                    matchPermission="any"
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path="/orders"
                    component={OrdersSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path="/multi-vendor-requests"
                    component={MultiVendorRequestsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_ORDERS]}
                    path="/special-requests"
                    component={SpecialRequestsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path="/products"
                    component={ProductSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES]}
                    path="/industry-types"
                    component={ProductTypesSection}
                  />
                  <SectionRoute path="/staff" component={StaffSection} />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_STAFF]}
                    path="/permission-groups"
                    component={PermissionGroupSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path="/site-settings"
                    component={SiteSettingsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SETTINGS]}
                    path={refundsSettingsPath}
                    component={RefundsSettingsRoute}
                  />
                  <SectionRoute path="/taxes" component={TaxesSection} />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_SHIPPING]}
                    path="/shipping"
                    component={ShippingSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_TRANSLATIONS]}
                    path="/translations"
                    component={TranslationsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_MENUS]}
                    path={structuresListPath}
                    component={NavigationSection}
                  />
                  <SectionRoute
                    permissions={[
                      PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
                      PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
                    ]}
                    path={attributeSection}
                    component={AttributeSection}
                    matchPermission="any"
                  />
                  <SectionRoute
                    permissions={[]}
                    path={extensionsSection}
                    component={ExtensionsSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_PRODUCTS]}
                    path={warehouseSection}
                    component={WarehouseSection}
                  />
                  <SectionRoute
                    permissions={[PermissionEnum.MANAGE_CHANNELS]}
                    path={channelsSection}
                    component={ChannelsSection}
                  />
                  <SectionRoute
                    matchPermission="any"
                    permissions={getConfigMenuItemsPermissions(intl)}
                    path="/configuration"
                    component={ConfigurationSection}
                  />
                  <Redirect to={ExtensionsPaths.installedExtensions} path={"/apps"} />
                  <Redirect to={ExtensionsPaths.installedExtensions} path="/custom-apps/" />
                  <Redirect to={ExtensionsPaths.installedExtensions} path="/plugins" />
                  <Route component={NotFound} />
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </AppLayout>
        </AppExtensionPopupProvider>
      ) : homePageLoading ? (
        <LoginLoading />
      ) : (
        <Suspense fallback={<LoginLoading />}>
          <Auth />
        </Suspense>
      )}
    </>
  );
};

const root = createRoot(document.querySelector("#dashboard-app")!);

// StrictMode is development-only (no effect in production)
// Set VITE_DISABLE_STRICT_MODE=true to disable for testing
const enableStrictMode = import.meta.env.DEV && import.meta.env.VITE_DISABLE_STRICT_MODE !== "true";

root.render(
  enableStrictMode ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <App />
  ),
);
